// This file will contain the logic for updating the delivery options.
document.addEventListener('DOMContentLoaded', function() {
  // Define blackout dates for each service type
  // Format: ['YYYY-MM-DD', 'YYYY-MM-DD', ...]
  const blackoutDates = {
    shipping: [
      '2025-11-26', // Wed before Thanksgiving
      '2025-11-27', // Thanksgiving
      // Add more shipping blackout dates here
    ],
    pickup: [
      '2025-11-27', // Thanksgiving only
      // Add more pickup blackout dates here
    ],
    delivery: [
      // Delivery has no blackout dates for Thanksgiving
      // Add delivery blackout dates here if needed
    ]
  };

  function getArizonaTime() {
    const now = new Date();
    const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
    return new Date(utc - (3600000 * 7)); // MST is UTC-7
  }

  function formatDateString(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  function isBlackoutDate(date, serviceType) {
    const dateString = formatDateString(date);
    return blackoutDates[serviceType] && blackoutDates[serviceType].includes(dateString);
  }

  function getNextAvailableDate(startDate, serviceType, skipWeekends = false) {
    const date = new Date(startDate);
    let daysToAdd = 1;

    // Keep adding days until we find a non-blackout date
    while (daysToAdd < 30) { // Safety limit to prevent infinite loop
      date.setDate(startDate.getDate() + daysToAdd);

      if (!isBlackoutDate(date, serviceType)) {
        // For shipping, also skip weekends
        if (skipWeekends && serviceType === 'shipping') {
          const dayOfWeek = date.getDay();
          if (dayOfWeek !== 0 && dayOfWeek !== 6) { // Not Sunday (0) or Saturday (6)
            return date;
          }
        } else {
          return date;
        }
      }
      daysToAdd++;
    }

    return date;
  }

  function getNextAvailableShippingDay(startDate) {
    const date = new Date(startDate);
    let daysToAdd = 1;

    // Keep adding days until we find a valid shipping day (Mon-Thu, not blackout)
    while (daysToAdd < 30) { // Safety limit to prevent infinite loop
      date.setDate(startDate.getDate() + daysToAdd);
      const dayOfWeek = date.getDay();

      // Check if it's a valid shipping day: Monday (1) through Thursday (4)
      if (dayOfWeek >= 1 && dayOfWeek <= 4 && !isBlackoutDate(date, 'shipping')) {
        return date;
      }
      daysToAdd++;
    }

    return date;
  }

  function formatDayName(date, referenceDate) {
    const msPerDay = 24 * 60 * 60 * 1000;
    const dayDiff = Math.floor((date - referenceDate) / msPerDay);

    if (dayDiff === 0) return 'Today';
    if (dayDiff === 1) return 'Tomorrow';

    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[date.getDay()];
  }

  function updateDeliveryDates() {
    const arizonaTime = getArizonaTime();
    const dayOfWeek = arizonaTime.getDay(); // Sunday - 0, Monday - 1, etc.
    const hourOfDay = arizonaTime.getHours();
    const minuteOfDay = arizonaTime.getMinutes();

    // Shipping Logic with blackout dates
    // We only ship Monday - Thursday
    let shipsBy = '';
    let nextShipDate;

    // Check current day and determine next shipping day
    if (dayOfWeek >= 0 && dayOfWeek <= 3) { // Sunday - Wednesday
      // Next shipping day would normally be tomorrow (Mon-Thu)
      const tomorrow = new Date(arizonaTime);
      tomorrow.setDate(tomorrow.getDate() + 1);

      // Check if tomorrow is a blackout date or not a shipping day (Fri-Sun)
      const tomorrowDay = tomorrow.getDay();
      if (isBlackoutDate(tomorrow, 'shipping') || tomorrowDay === 5 || tomorrowDay === 6 || tomorrowDay === 0) {
        // Find next available shipping day (Mon-Thu, not blackout)
        nextShipDate = getNextAvailableShippingDay(arizonaTime);
        shipsBy = formatDayName(nextShipDate, arizonaTime);
      } else {
        nextShipDate = tomorrow;
        shipsBy = 'Tomorrow';
      }
    } else { // Thursday - Saturday
      // Next shipping day is Monday or later
      nextShipDate = getNextAvailableShippingDay(arizonaTime);
      shipsBy = formatDayName(nextShipDate, arizonaTime);
    }

    // Delivery Logic with blackout dates
    let deliveryDay = '';

    // For delivery, we use the normal weekend logic since delivery is unaffected by Thanksgiving
    if (dayOfWeek === 5 && hourOfDay < 14) { // Friday before 2pm
      deliveryDay = 'Saturday';
    } else if ((dayOfWeek === 5 && hourOfDay >= 14) || (dayOfWeek === 6 && hourOfDay < 14)) { // Friday after 2pm or Saturday before 2pm
      deliveryDay = 'Sunday';
    } else if ((dayOfWeek === 6 && hourOfDay >= 14) || (dayOfWeek === 0 && hourOfDay < 14)) { // Saturday after 2pm or Sunday before 2pm
      deliveryDay = 'Monday';
    } else {
      deliveryDay = 'Saturday';
    }

    // If delivery has blackout dates, check them
    if (blackoutDates.delivery && blackoutDates.delivery.length > 0) {
      const deliveryDate = new Date(arizonaTime);
      const daysMap = {'Today': 0, 'Tomorrow': 1, 'Saturday': null, 'Sunday': null, 'Monday': null};

      // Calculate actual date for the delivery day
      if (daysMap[deliveryDay] !== null && daysMap[deliveryDay] !== undefined) {
        deliveryDate.setDate(deliveryDate.getDate() + daysMap[deliveryDay]);
      } else {
        // Find next occurrence of the day
        const targetDays = {'Saturday': 6, 'Sunday': 0, 'Monday': 1};
        const targetDay = targetDays[deliveryDay];
        const daysUntil = (targetDay - dayOfWeek + 7) % 7 || 7;
        deliveryDate.setDate(deliveryDate.getDate() + daysUntil);
      }

      // Check if delivery date is blocked and find next available
      if (isBlackoutDate(deliveryDate, 'delivery')) {
        const nextDeliveryDate = getNextAvailableDate(deliveryDate, 'delivery', false);
        deliveryDay = formatDayName(nextDeliveryDate, arizonaTime);
      }
    }

    // Pickup Logic with blackout dates
    let pickupDay = '';
    const pickupCutoffHour = 11;
    const pickupCutoffMinute = 0;

    if (isBlackoutDate(arizonaTime, 'pickup')) {
      // During blackout period, pickups resume on next available day
      const nextPickupDate = getNextAvailableDate(arizonaTime, 'pickup', false);
      pickupDay = formatDayName(nextPickupDate, arizonaTime);
    } else {
      if (hourOfDay < pickupCutoffHour || (hourOfDay === pickupCutoffHour && minuteOfDay < pickupCutoffMinute)) {
        // Before 11 AM cutoff - check if today is available
        if (!isBlackoutDate(arizonaTime, 'pickup')) {
          pickupDay = 'Today';
        } else {
          const nextPickupDate = getNextAvailableDate(arizonaTime, 'pickup', false);
          pickupDay = formatDayName(nextPickupDate, arizonaTime);
        }
      } else {
        // After 11 AM cutoff - check if tomorrow is available
        const tomorrow = new Date(arizonaTime);
        tomorrow.setDate(tomorrow.getDate() + 1);

        if (!isBlackoutDate(tomorrow, 'pickup')) {
          pickupDay = 'Tomorrow';
        } else {
          const nextPickupDate = getNextAvailableDate(arizonaTime, 'pickup', false);
          pickupDay = formatDayName(nextPickupDate, arizonaTime);
        }
      }
    }

    // Update the HTML
    const shipsByElement = document.getElementById('ships-by-day');
    if (shipsByElement) {
      shipsByElement.textContent = shipsBy;
    }

    const deliveryDayElement = document.getElementById('delivery-day');
    if (deliveryDayElement) {
      deliveryDayElement.textContent = deliveryDay;
      // deliveryDayElement.textContent = 'Sunday';
    }

    const pickupDayElement = document.getElementById('pickup-day');
    if (pickupDayElement) {
      pickupDayElement.textContent = pickupDay;
    }
  }

  updateDeliveryDates();
}); 